import { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Select, Skeleton } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ContentBackground, ContentBody, ContentHeader, ContentWrapper } from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import ClientSideContactsPanel from '@/components/team-assignments/ClientSideContactsPanel';
import { ROLES, ROLES_PERMISSIONS, ROUTES } from '@/constants';
import { listMyClientSideCompanies } from '@/services/teamAssignments.service';
import { useAuthStore } from '@/store/useAuthStore';

/**
 * Where the mail to clients leads. Someone who runs the company on the platform
 * goes on to the tile on its profile. A person who only has an account for the
 * contacts has no profile to open, so the same list stands here on its own; the
 * page has no menu entry.
 */
function MyContactsContent() {
  const { t } = useTranslation('teamContacts');
  const roles = useAuthStore((state) => state.user?.roles) || [];
  const [params] = useSearchParams();
  const asked = params.get('company');
  const contactOnly = !roles.includes(ROLES.client) && !roles.includes(ROLES.clientOfficer);
  const [companyId, setCompanyId] = useState(asked);

  const { data, isLoading } = useQuery({
    queryKey: ['client-side-companies'],
    queryFn: listMyClientSideCompanies,
    enabled: contactOnly,
    retry: false,
  });
  const items = useMemo(() => data?.items || [], [data]);

  useEffect(() => {
    if (items.length && !items.some((c) => c._id === companyId)) setCompanyId(items[0]._id);
  }, [items, companyId]);

  if (!contactOnly && asked) {
    return <Navigate to={`${ROUTES.admin}/${ROUTES.companies}/${asked}?tile=kontakti`} replace />;
  }
  if (!contactOnly) return <Navigate to={`${ROUTES.admin}/${ROUTES.companies}`} replace />;

  return (
    <RootLayout metadata={{ title: t('client.title') }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('client.title')} />
            <ContentBody>
              <div className="with-y-overflow overflow-x-hidden">
                <div className="grid grid-cols-[minmax(0,1fr)] gap-4 p-4">
                  {isLoading && <Skeleton active />}
                  {!isLoading && !items.length && <span className="opacity-60">{t('client.noCompanies')}</span>}
                  {items.length > 1 && (
                    <Select
                      className="w-full max-w-[420px]"
                      value={companyId}
                      onChange={setCompanyId}
                      options={items.map((c) => ({ value: c._id, label: c.name }))}
                    />
                  )}
                  {items.length === 1 && <h3 className="text-base font-semibold">{items[0].name}</h3>}
                  {companyId && items.some((c) => c._id === companyId) && <ClientSideContactsPanel key={companyId} companyId={companyId} />}
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

const MyContactsPage = withPrivateRoute(MyContactsContent, ROLES_PERMISSIONS.CLIENT_CONTACTS.VIEW);
MyContactsPage.displayName = 'MyContactsPage';

export default MyContactsPage;
